import type { OpportunityIteration } from '../types';
import { formatDateLong } from '../utils/formatters';
import styles from './OpportunityScoreTrend.module.css';

function triggerLabel(trigger: OpportunityIteration['trigger']): string {
  switch (trigger) {
    case 'analyze':
      return 'Analysis';
    case 'refine':
      return 'Refinement';
    case 'resolution':
      return 'Fix applied';
    default:
      return trigger;
  }
}

export function OpportunityScoreTrend({ iterations }: { iterations?: OpportunityIteration[] }) {
  const entries = iterations ?? [];
  if (entries.length === 0) {
    return null;
  }

  const first = entries[0]!;
  const latest = entries[entries.length - 1]!;
  const delta = latest.overallScore - first.overallScore;
  const deltaLabel = `${delta >= 0 ? '+' : ''}${delta.toFixed(1)}`;

  return (
    <section className={styles.card} aria-label="Score trend">
      <div className={styles.header}>
        <div>
          <p className={styles.label}>Score trend</p>
          <p className={styles.value}>
            {latest.overallScore.toFixed(1)}
            <span className={styles.valueMax}>/10</span>
          </p>
        </div>
        {entries.length > 1 ? (
          <span
            className={`${styles.delta} ${
              delta > 0 ? styles.deltaUp : delta < 0 ? styles.deltaDown : styles.deltaFlat
            }`}
          >
            {deltaLabel} since first analysis
          </span>
        ) : (
          <span className={`${styles.delta} ${styles.deltaFlat}`}>
            First analysis — fix weak points to move the score
          </span>
        )}
      </div>
      <div className={styles.bars} role="img" aria-label={`Score across ${entries.length} iterations`}>
        {entries.map((entry, index) => (
          <div
            key={entry.id}
            className={styles.barColumn}
            title={`${triggerLabel(entry.trigger)} · ${formatDateLong(entry.createdAt)} · ${entry.overallScore.toFixed(1)}/10\n${entry.summary}`}
          >
            <div className={styles.barTrack}>
              <span
                className={`${styles.barFill} ${index === entries.length - 1 ? styles.barFillLatest : ''}`}
                style={{ height: `${Math.min(100, Math.max(4, (entry.overallScore / 10) * 100))}%` }}
              />
            </div>
            <span className={styles.barScore}>{entry.overallScore.toFixed(1)}</span>
          </div>
        ))}
      </div>
    </section>
  );
}
